import { Button } from "@components/button";
import { ArrowIcon } from "@components/icon";
import { isDefined, isNotDefined } from "@utils/general";
import { KeyboardShortcut } from "@utils/keyboardShortcutsManager";
import KeyboardShortcutsManager from "@utils/keyboardShortcutsManager/KeyboardShortcutsManager";
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";

import { IconSize } from "../../enums";

export interface IStepChangeArgs {
    currentStep: number;
    nextStep?: number;
    isComplete?: boolean;
}

interface IProps {
    step?: number;
    steps: string[];
    onCancel: () => void;
    onConfirm: () => void;
    canChangeStep?: (args: IStepChangeArgs) => Promise<boolean>;
    onChangeStep?: (nextStep: number, currentStep: number) => void;
    children: (step: number, title?: string, buttons?: React.ReactElement) => React.ReactElement;
    confirmText?: string;
    isConfirmDisabled?: boolean;
}

const Wizard: React.FC<IProps> = ({
                                      step,
                                      steps,
                                      canChangeStep,
                                      children,
                                      onConfirm,
                                      onCancel,
                                      onChangeStep,
                                      confirmText,
                                      isConfirmDisabled
                                  }) => {
    const { t } = useTranslation("Components");
    const [currentStep, setStep] = useState<number>();

    useEffect(() => {
        // Initialize the first step or any other setup logic
        setStep(step ?? 0);
    }, [step]);

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === steps.length - 1;

    const processStepChange = useCallback((nextStep: number | null) => {
        if (isDefined(nextStep)) {
            setStep(nextStep);
            onChangeStep?.(nextStep, currentStep);
        } else {
            onConfirm();
        }
    }, [onChangeStep, currentStep, onConfirm]);

    const confirmStepChange = useCallback(async (nextStep: number) => {
        if (nextStep < 0 || nextStep >= steps.length) {
            return;
        }

        const args: IStepChangeArgs = {
            currentStep: currentStep,
            nextStep: nextStep,
            isComplete: isNotDefined(nextStep)
        };

        if (typeof canChangeStep === 'function') {
            const shouldProceed = await canChangeStep(args);
            if (shouldProceed !== false) {
                processStepChange(nextStep);
            }
        } else {
            processStepChange(nextStep);
        }
    }, [steps.length, currentStep, canChangeStep, processStepChange]);

    const onNextStep = useCallback(() => {
        if (isLastStep && isConfirmDisabled) {
            return;
        }
        confirmStepChange(isLastStep ? null : currentStep + 1);
    }, [isLastStep, isConfirmDisabled, confirmStepChange, currentStep]);

    const onPreviousStep = useCallback(() => {
        if (!isFirstStep) {
            confirmStepChange(currentStep - 1);
        }
    }, [currentStep, isFirstStep, confirmStepChange]);

    const handleKeyboardShortcut = useCallback((shortcut: KeyboardShortcut, event: KeyboardEvent) => {
        if (shortcut === KeyboardShortcut.ALT_S) {
            onNextStep();
            return true;
        }
        return false;
    }, [onNextStep]);

    useEffect(() => {
        return KeyboardShortcutsManager.subscribe({
            shortcuts: [{ id: KeyboardShortcut.ALT_S }],
            callback: handleKeyboardShortcut
        });
    }, [handleKeyboardShortcut]);

    const title = `${currentStep+1}/${steps.length}: ${steps[currentStep]}`;

    const buttons = (<>
        {!isFirstStep && (
                <Button onClick={onPreviousStep} icon={<ArrowIcon width={IconSize.S}/>} isTransparent>
                    {t("Wizard.Back")}
                </Button>
        )}
        <Button onClick={onCancel} isTransparent>{t("Wizard.Cancel")}</Button>
        <Button isDisabled={isLastStep && isConfirmDisabled}
                onClick={onNextStep}>
            {isLastStep ? confirmText ?? t("Wizard.Complete") : t("Wizard.Continue")}
        </Button>
    </>);

    return children(currentStep, title, buttons);
};

export default Wizard;